import {
  getSidebarIconButtonSx,
  getSidenavTextColor,
} from '@app/components/layout/sidenav/sidenav-colors';
import { SIDENAV_TOKENS } from '@app/components/layout/sidenav/sidenav-tokens';
import { blinkerAnimation } from '@app/core/ui/animations';
import CloudDoneOutlinedIcon from '@mui/icons-material/CloudDoneOutlined';
import CloudDownloadOutlinedIcon from '@mui/icons-material/CloudDownloadOutlined';
import { alpha, IconButton, Tooltip } from '@mui/material';
import { defineMessage, useIntl } from 'react-intl';
import { config } from '../../config';
import { useVersionCheck } from './useVersionCheck';

const versionUpdateTooltipMessage = defineMessage({
  id: 'ui.versionChecker.newVersionAvailableReload',
  defaultMessage: 'Nueva versión disponible, click para actualizar',
});

const versionUpToDateTooltipMessage = defineMessage({
  id: 'ui.versionChecker.appUpToDate',
  defaultMessage: 'La app está actualizada ({version})',
});

/**
 * Compact version status control on the identity row.
 * Click reloads only when a new build is available.
 */
export function VersionUpdateSidebarButton() {
  const { isNewVersionAvailable, reload } = useVersionCheck();
  const intl = useIntl();

  const versionLabel = config.isAppDev ? `${config.appVersion} · local` : config.appVersion;
  const tooltip = isNewVersionAvailable
    ? intl.formatMessage(versionUpdateTooltipMessage)
    : intl.formatMessage(versionUpToDateTooltipMessage, { version: versionLabel });

  const Icon = isNewVersionAvailable ? CloudDownloadOutlinedIcon : CloudDoneOutlinedIcon;

  return (
    <Tooltip title={tooltip} placement="right">
      <IconButton
        size="small"
        onClick={isNewVersionAvailable ? () => reload('sidebar') : undefined}
        disableRipple={!isNewVersionAvailable}
        sx={(theme) => {
          const textColor = getSidenavTextColor(theme);
          const mutedColor = alpha(textColor, 0.55);

          return {
            ...getSidebarIconButtonSx(theme),
            color: isNewVersionAvailable ? theme.palette.warning.light : mutedColor,
            cursor: isNewVersionAvailable ? 'pointer' : 'default',
            ...(isNewVersionAvailable
              ? {
                  animation: blinkerAnimation,
                  // Keep warning color on hover; getSidebarIconButtonSx would reset to sidenav text.
                  '&:hover': { color: theme.palette.warning.light },
                  '&:focus': { color: theme.palette.warning.light },
                }
              : {
                  '&:hover': { background: 'transparent', color: mutedColor },
                  '&:focus': { background: 'transparent', color: mutedColor },
                }),
          };
        }}
      >
        <Icon sx={{ fontSize: SIDENAV_TOKENS.iconSize }} />
      </IconButton>
    </Tooltip>
  );
}
